import { useMemo } from 'react';
import { t } from 'i18next';
import { CategoriesStatsResponse } from '../../../shared/models/monitor/data/sources/CategoriesStatsResponse';
import { StackedBarData } from '../../../shared/components/chart/stacked-horizontal-bar-chart/StackedHorizontalBarChart';
import { getFirstSeriesPalette } from '../../../shared/utils/chartTheme';

interface CitationTypesData {
  chartData: StackedBarData[];
  shouldShow: boolean;
}

const LEGACY_TRANSLATION_MAP: Record<string, string> = {
  Owned: 'citationTypeOwned',
  News: 'categoryNews',
  Blogs: 'categoryBlogs',
  Videos: 'categoryVideos',
  Photos: 'categoryPhotos',
  Social: 'categorySocial',
  Podcasts: 'categoryPodcasts',
  Others: 'categoryWeb',
};

const SOURCE_CATEGORY_TRANSLATION_MAP: Record<string, string> = {
  Owned: 'citationTypeOwned',
  CORPORATE: 'sourceCategoryCorporate',
  EDITORIAL: 'sourceCategoryEditorial',
  INSTITUTIONAL: 'sourceCategoryInstitutional',
  UGC: 'sourceCategoryUgc',
  REFERENCE: 'sourceCategoryReference',
  VIDEO: 'sourceCategoryVideo',
  OTHER: 'sourceCategoryOther',
};

const HIDE_CATEGORY = {
  legacy: 'Others',
  sourceCategory: 'OTHER',
};

const COMPETITOR_COLOR = '#cd3d44';

export const useCitationTypesShareData = (
  categoriesStats: CategoriesStatsResponse | undefined,
  useSourceCategories: boolean = false,
  competitorCount: number = 0
): CitationTypesData => {
  return useMemo(() => {
    if (!categoriesStats || !categoriesStats.categories || categoriesStats.total === 0) {
      return { chartData: [], shouldShow: false };
    }

    const nonZeroCategories = categoriesStats.categories.filter((stat) => stat.count > 0);

    if (nonZeroCategories.length === 0) {
      return { chartData: [], shouldShow: false };
    }

    const hideCategoryName = useSourceCategories ? HIDE_CATEGORY.sourceCategory : HIDE_CATEGORY.legacy;

    // If only one category and it's "Others"/"OTHER" with ~100%, hide it
    if (nonZeroCategories.length === 1 && competitorCount === 0) {
      const percentage = (nonZeroCategories[0].count / categoriesStats.total) * 100;
      if (nonZeroCategories[0].category === hideCategoryName && percentage >= 99.9) {
        return { chartData: [], shouldShow: false };
      }
    }

    const translationMap = useSourceCategories ? SOURCE_CATEGORY_TRANSLATION_MAP : LEGACY_TRANSLATION_MAP;
    const total = categoriesStats.total;

    // Build base chart data
    const baseCategories = categoriesStats.categories
      .filter((stat) => stat.count > 0)
      .sort((a, b) => b.count - a.count);

    // When competitor count is provided, scale down remaining categories proportionally
    // so that competitor + scaled categories = 100%
    const nonCompetitorTotal = total - competitorCount;
    const adjustedCategories = competitorCount > 0 && nonCompetitorTotal > 0
      ? baseCategories.map((stat) => ({
          ...stat,
          count: Math.round((stat.count / total) * nonCompetitorTotal),
        })).filter((stat) => stat.count > 0)
      : baseCategories;

    const chartData: StackedBarData[] = [];

    // Add competitor as first item if present
    if (competitorCount > 0) {
      chartData.push({
        label: t('sourceCategoryCompetitor'),
        color: COMPETITOR_COLOR,
        value: Number(((competitorCount / total) * 100).toFixed(2)),
      });
    }

    adjustedCategories.forEach((stat) => {
      const colorIndex = chartData.length;
      chartData.push({
        label: t(translationMap[stat.category] || stat.category),
        color: getFirstSeriesPalette()[colorIndex % getFirstSeriesPalette().length],
        value: Number(((stat.count / total) * 100).toFixed(2)),
      });
    });

    return { chartData, shouldShow: true };
  }, [categoriesStats, useSourceCategories, competitorCount]);
};
